import { useState, useEffect } from 'react';
import type { Project, Client } from '@/types';

interface ProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<Project>) => void;
  project: Project | null;
  clients: Client[];
  isProcessing: boolean;
}

export const ProjectModal = ({
  isOpen,
  onClose,
  onSubmit,
  project,
  clients,
  isProcessing,
}: ProjectModalProps) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    clientId: '',
    description: '',
    status: 'active' as 'active' | 'archived' | 'completed',
    // 🆕 請求設定
    contractType: 'hourly' as 'hourly' | 'monthly' | undefined,
    unitPrice: '',
    monthlyFixedRate: '',
    billingCycle: '1',
    calculationMethod: 'floor' as 'floor' | 'round' | 'ceil',
    billingAdjustmentType: 'per_item' as 'per_item' | 'monthly_total',
    monthlyBaseHours: '',
    monthlyMinHours: '',
    monthlyMaxHours: '',
  });

  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name || '',
        code: project.code || '',
        clientId: project.clientId || '',
        description: project.description || '',
        status: project.status,
        // 🆕 請求設定
        contractType: project.contractType,
        unitPrice: project.unitPrice?.toString() || '',
        monthlyFixedRate: project.monthlyFixedRate?.toString() || '',
        billingCycle: project.billingCycle?.toString() || '1',
        calculationMethod: project.calculationMethod || 'floor',
        billingAdjustmentType: project.billingAdjustmentType || 'per_item',
        monthlyBaseHours: project.monthlyBaseHours?.toString() || '',
        monthlyMinHours: project.monthlyMinHours?.toString() || '',
        monthlyMaxHours: project.monthlyMaxHours?.toString() || '',
      });
    } else {
      setFormData({
        name: '',
        code: '',
        clientId: '',
        description: '',
        status: 'active',
        contractType: 'hourly',
        unitPrice: '',
        monthlyFixedRate: '',
        billingCycle: '1',
        calculationMethod: 'floor',
        billingAdjustmentType: 'per_item',
        monthlyBaseHours: '',
        monthlyMinHours: '',
        monthlyMaxHours: '',
      });
    }
  }, [project]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const selectedClient = clients.find(c => c.id === formData.clientId);
    
    // 🆕 数値フィールドの変換
    const submitData: any = {
      name: formData.name,
      code: formData.code,
      clientId: formData.clientId,
      description: formData.description,
      status: formData.status,
      client: selectedClient ? {
        id: selectedClient.id,
        name: selectedClient.name,
      } : undefined,
    };

    // 請求設定を追加（値がある場合のみ）
    if (formData.contractType) {
      submitData.contractType = formData.contractType;
      
      if (formData.unitPrice) {
        submitData.unitPrice = parseFloat(formData.unitPrice);
      }
      
      if (formData.contractType === 'monthly') {
        if (formData.monthlyFixedRate) {
          submitData.monthlyFixedRate = parseFloat(formData.monthlyFixedRate);
        }
        if (formData.monthlyBaseHours) {
          submitData.monthlyBaseHours = parseFloat(formData.monthlyBaseHours);
        }
        if (formData.monthlyMinHours) {
          submitData.monthlyMinHours = parseFloat(formData.monthlyMinHours);
        }
        if (formData.monthlyMaxHours) {
          submitData.monthlyMaxHours = parseFloat(formData.monthlyMaxHours);
        }
      }
      
      if (formData.billingCycle) {
        submitData.billingCycle = parseInt(formData.billingCycle);
      }
      
      submitData.calculationMethod = formData.calculationMethod;
      submitData.billingAdjustmentType = formData.billingAdjustmentType;
    }
    
    onSubmit(submitData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-500 px-6 py-4">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">
                {project ? 'プロジェクト編集' : '新規プロジェクト'}
              </h2>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-white/20 transition-colors"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Project Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                プロジェクト名 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                placeholder="例: Webサイトリニューアル"
                required
                disabled={isProcessing}
              />
            </div>

            {/* Project Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                プロジェクトコード
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                placeholder="例: PROJ-2024-001"
                disabled={isProcessing}
              />
            </div>

            {/* Client Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                クライアント
              </label>
              <select
                value={formData.clientId}
                onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                disabled={isProcessing}
              >
                <option value="">クライアントを選択</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ステータス
              </label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: 'active', label: 'アクティブ', color: 'green' },
                  { value: 'completed', label: '完了', color: 'blue' },
                  { value: 'archived', label: 'アーカイブ', color: 'gray' },
                ].map((status) => (
                  <label
                    key={status.value}
                    className={`relative flex items-center justify-center px-4 py-3 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.status === status.value
                        ? `border-${status.color}-500 bg-${status.color}-50`
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="status"
                      value={status.value}
                      checked={formData.status === status.value}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                      className="sr-only"
                      disabled={isProcessing}
                    />
                    <span className={`text-sm font-medium ${
                      formData.status === status.value ? `text-${status.color}-700` : 'text-gray-700'
                    }`}>
                      {status.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                説明
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none"
                placeholder="プロジェクトの詳細を入力..."
                disabled={isProcessing}
              />
            </div>

            {/* 🆕 請求設定セクション */}
            <div className="pt-6 border-t-2 border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                請求設定
              </h3>

              {/* 契約タイプ */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  契約タイプ
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { value: 'hourly', label: '時給', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
                    { value: 'monthly', label: '月額固定', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
                  ].map((type) => (
                    <label
                      key={type.value}
                      className={`relative flex items-center gap-3 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all ${
                        formData.contractType === type.value
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="contractType"
                        value={type.value}
                        checked={formData.contractType === type.value}
                        onChange={(e) => setFormData({ ...formData, contractType: e.target.value as any })}
                        className="sr-only"
                        disabled={isProcessing}
                      />
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={type.icon} />
                      </svg>
                      <span className={`text-sm font-medium ${
                        formData.contractType === type.value ? 'text-primary-700' : 'text-gray-700'
                      }`}>
                        {type.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* 時給設定 */}
              {formData.contractType && (
                <>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        時給単価 (円)
                        {formData.contractType === 'hourly' && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      <input
                        type="number"
                        value={formData.unitPrice}
                        onChange={(e) => setFormData({ ...formData, unitPrice: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                        placeholder="例: 3000"
                        min="0"
                        step="100"
                        required={formData.contractType === 'hourly'}
                        disabled={isProcessing}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        端数処理
                      </label>
                      <select
                        value={formData.calculationMethod}
                        onChange={(e) => setFormData({ ...formData, calculationMethod: e.target.value as any })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                        disabled={isProcessing}
                      >
                        <option value="floor">切り捨て</option>
                        <option value="round">四捨五入</option>
                        <option value="ceil">切り上げ</option>
                      </select>
                    </div>
                  </div>

                  {/* 🆕 端数処理タイミング＆サイクル */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        端数処理タイミング
                      </label>
                      <select
                        value={formData.billingAdjustmentType}
                        onChange={(e) => setFormData({ ...formData, billingAdjustmentType: e.target.value as any })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                        disabled={isProcessing}
                      >
                        <option value="per_item">明細単位（各明細ごと）</option>
                        <option value="monthly_total">月次単位（月合計）</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        端数処理サイクル (分)
                      </label>
                      <select
                        value={formData.billingCycle}
                        onChange={(e) => setFormData({ ...formData, billingCycle: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                        disabled={isProcessing}
                      >
                        <option value="1">1分単位</option>
                        <option value="15">15分単位</option>
                        <option value="30">30分単位</option>
                        <option value="60">60分単位（1時間）</option>
                      </select>
                    </div>
                  </div>

                  {/* 説明テキスト */}
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                    <p className="text-xs text-blue-800">
                      <strong>明細単位:</strong> 各稼働記録ごとに端数処理を適用します（例: 2.3h → 2h、1.8h → 1h）<br />
                      <strong>月次単位:</strong> 月合計に対して端数処理を適用します（例: 合計40.5h → 40h）
                    </p>
                  </div>
                </>
              )}

              {/* 月額固定設定 */}
              {formData.contractType === 'monthly' && (
                <>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        月額固定料金 (円) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={formData.monthlyFixedRate}
                        onChange={(e) => setFormData({ ...formData, monthlyFixedRate: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                        placeholder="例: 300000"
                        min="0"
                        step="10000"
                        required
                        disabled={isProcessing}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        基準時間 (時間)
                      </label>
                      <input
                        type="number"
                        value={formData.monthlyBaseHours}
                        onChange={(e) => setFormData({ ...formData, monthlyBaseHours: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                        placeholder="例: 160"
                        min="0"
                        step="1"
                        disabled={isProcessing}
                      />
                    </div>
                  </div>

                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl mb-4">
                    <p className="text-sm text-blue-800 font-medium mb-2">💡 レンジ設定（オプション）</p>
                    <p className="text-xs text-blue-700 mb-3">
                      稼働時間の上限・下限を設定できます。範囲外の場合は時給計算が適用されます。
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-blue-900 mb-1">
                          下限時間 (時間)
                        </label>
                        <input
                          type="number"
                          value={formData.monthlyMinHours}
                          onChange={(e) => setFormData({ ...formData, monthlyMinHours: e.target.value })}
                          className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          placeholder="例: 120"
                          min="0"
                          step="1"
                          disabled={isProcessing}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-blue-900 mb-1">
                          上限時間 (時間)
                        </label>
                        <input
                          type="number"
                          value={formData.monthlyMaxHours}
                          onChange={(e) => setFormData({ ...formData, monthlyMaxHours: e.target.value })}
                          className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          placeholder="例: 180"
                          min="0"
                          step="1"
                          disabled={isProcessing}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-blue-600 mt-2">
                      ※ 超過分は時給単価で計算されます
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 active:scale-95 transition-all"
                disabled={isProcessing}
              >
                キャンセル
              </button>
              <button
                type="submit"
                className="flex-1 px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-500 text-white font-medium rounded-xl hover:from-primary-700 hover:to-primary-600 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    処理中...
                  </span>
                ) : (
                  project ? '更新' : '作成'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
